import { Table } from "antd";
import moment from "moment-timezone";
import React, { useState } from "react";
import { withTranslation } from "react-i18next";
import { connect } from "react-redux";
import { compose } from "redux";
import { createStructuredSelector } from "reselect";
import styled from "styled-components";

import { isNull } from "lodash";
import { ApiNames } from "../../api/api";
import { SUPPORTED_COVERAGES } from "../../constant/patient";
import { Routes } from "../../constant/routes";
import { PAGE_SIZE, selectedViews } from "../../constant/table";
import { PageKeys } from "../../utils/pendo/useStandardizedTracking";
import ACTION from "../../store/action";
import { selectAllApplicationsData, selectIsFetching, selectTableColumns } from "../../store/selector";
import { dateFormatter } from "../../utils/date";
import { fetchTableData } from "../../utils/fetchTableData";
import { formatToDecimal } from "../../utils/formaters";
import { ColumnSelector } from "../ColumnSelector";
import { ESignatureStatusBadge } from "../Patient/PatientApplications/enrollTab/utils/components";
import { patientInsuranceCell } from "../TableCells";
import { TailormedSpinner } from "../TailormedSpinner/TailormedSpinner";
import LinkableCell from "../UI/LinkableCell";
import {
  DrugColumnComponent as AssociatedDrugColumn,
  EditColumn,
  PatientNameColumn,
  StarColumn,
  withSorting
} from "./CommonColumns";
import ConsentStatusColumn from "./ConsentStatusColumn";
import EmptyTableMessage from "./EmptyTableMessage";
import PatientPageCount from "./PatientPageCount";
import { ColumnSelectorContainer } from "./TableStyle";
import { commonBulkActionsTableStyle } from "./BulkActionsTableStyle";
import { useGetSetting } from "../../hooks/getSetting";
import useTableInlineEdit from "../../hooks/InlineEdit/useTableInlineEdit";
import useColumnResizeAndReorder from "../../hooks/useColumnResizeAndReorder";
import { TableColumnWrapper } from "@tailormed/common-client/component/ColumnResizeAndReorder/TableColumnWrapper";
import { ColumnsKeys } from "../../constant/columns";

const StyledTable = styled(Table)`
  ${(props) => commonBulkActionsTableStyle(props.isFilterListView, props.$hasSelection)}
`;

const renderWithLink = (patientRec, renderFn) => (
  <LinkableCell to={Routes.PATIENT_APPLICATION_PATH(patientRec)}>{renderFn()}</LinkableCell>
);

const AllApplicationsTableComponent = ({
  tableColumns,
  isFetching,
  setTableDataAct,
  tableData,
  t,
  updateSavedFilterViewParameters,
  isFilterListView,
  rowSelection
}) => {
  const totalRecordsCount = tableData?.totalPatients;
  const sortingEnabled = tableData?.sortingEnabled;
  const [isNewTrackTabAllowed] = useGetSetting(["fe.showNewTrackTab"]);
  const [hoveringLineIndex, setHoveringLineIndex] = useState(null);
  const { processColumns, components, containerRef, reorderGuideLeft, tableProps } = useColumnResizeAndReorder(
    ColumnsKeys.tables["all-applications"],
    ["star", "edit"]
  );

  const { renderPatientCell, renderEnrollmentCell } = useTableInlineEdit({
    t,
    view: selectedViews.allApplications,
    patientActionCreator: ACTION.updateEnrollmentsForPatientInPlace,
    enrollment: true,
    renderWithLink
  });

  const columns = [
    {
      dataIndex: tableColumns[selectedViews.allApplications]?.star?.dataIndex,
      key: tableColumns[selectedViews.allApplications]?.star?.key,
      width: 35,
      className: "star-column",
      render: (text, record) => <StarColumn record={record} />
    },
    withSorting(sortingEnabled, tableColumns[selectedViews.allApplications]?.name_id, {
      sorterTooltipText: t("columns.sortById"),
      render: (text, record) => renderWithLink(record, () => <PatientNameColumn record={record} />),
      minWidth: 300
    }),
    withSorting(sortingEnabled, tableColumns[selectedViews.allPatients]?.isDeceased, {
      render: (_, record) => renderPatientCell("isDeceased", record)
    }),
    {
      ...tableColumns[selectedViews.allApplications]?.phone_number,
      render: (text, record) => renderPatientCell("phone_number", record)
    },
    {
      ...tableColumns[selectedViews.allApplications]?.dob,
      render: (text, record) => renderPatientCell("dob", record)
    },
    {
      ...tableColumns[selectedViews.allApplications]?.ssn,
      render: (text, record) => renderPatientCell("ssn", record)
    },
    withSorting(sortingEnabled, tableColumns[selectedViews.allApplications]?.assistanceProgram, {
      render: (text, record) =>
        renderWithLink(record, () => (record.assistanceProgramName ? record.assistanceProgramName : null))
    }),
    withSorting(sortingEnabled, tableColumns[selectedViews.allApplications]?.providedBy, {
      render: (_, record) => renderWithLink(record, () => record.providedBy)
    }),
    withSorting(sortingEnabled, tableColumns[selectedViews.allApplications]?.apType, {
      render: (text, record) => renderWithLink(record, () => (record.apType ? record.apType : null))
    }),
    {
      ...tableColumns[selectedViews.allApplications]?.associatedDrug,
      render: (text, record) => renderWithLink(record, () => <AssociatedDrugColumn drugs={record.associatedDrugs} />)
    },
    withSorting(sortingEnabled, tableColumns[selectedViews.allApplications]?.applicationStatus, {
      width: 150,
      render: (text, record) => renderEnrollmentCell("applicationStatus", record)
    }),
    withSorting(sortingEnabled, tableColumns[selectedViews.allApplications]?.fundBalance, {
      render: (text, record) =>
        renderWithLink(record, () =>
          !isNull(record.fundBalance) ? `$${formatToDecimal(record.fundBalance) || 0} ` : null
        )
    }),
    withSorting(sortingEnabled, tableColumns[selectedViews.allApplications]?.validThrough, {
      width: 130,
      render: (text, record) => renderEnrollmentCell("validThrough", record)
    }),
    withSorting(sortingEnabled, tableColumns[selectedViews.allApplications]?.approvedAmount, {
      title: t(isNewTrackTabAllowed ? "patientApplications.max_benefit" : "patientApplications.approved_amount"),
      render: (text, record) => renderEnrollmentCell("approvedAmount", record)
    }),
    withSorting(sortingEnabled, tableColumns[selectedViews.allApplications]?.enrollmentCreationDate, {
      render: (text, record) =>
        renderWithLink(
          record,
          () => moment(record.enrollmentCreationDate).isValid() && dateFormatter(record.enrollmentCreationDate)
        )
    }),
    withSorting(sortingEnabled, tableColumns[selectedViews.allApplications]?.appliedDate, {
      render: (text, record) => renderEnrollmentCell("appliedDate", record)
    }),
    withSorting(sortingEnabled, tableColumns[selectedViews.allApplications]?.approvedDate, {
      render: (text, record) => renderEnrollmentCell("approvedDate", record)
    }),
    withSorting(sortingEnabled, tableColumns[selectedViews.allApplications]?.member_id, {
      render: (text, record) => renderEnrollmentCell("member_id", record)
    }),
    {
      ...tableColumns[selectedViews.allApplications]?.enrollmentAssignee,
      render: (text, record) => renderEnrollmentCell("enrollmentAssignee", record)
    },
    {
      ...tableColumns[selectedViews.allApplications]?.assignee,
      render: (text, record) => renderPatientCell("assignee", record)
    },
    {
      ...tableColumns[selectedViews.allApplications]?.facility,
      sorter: false,
      render: (_, record) => renderPatientCell("facility", record)
    },
    withSorting(sortingEnabled, tableColumns[selectedViews.allApplications]?.physician, {
      render: (_, record) => renderPatientCell("physician", record)
    }),
    withSorting(sortingEnabled, tableColumns[selectedViews.allApplications]?.patientStatus, {
      render: (_, record) => renderPatientCell("patientStatus", record)
    }),
    {
      ...tableColumns[selectedViews.allApplications]?.agreementSignatureDate,
      render: (text, record) =>
        renderWithLink(
          record,
          () => moment(record.activeConsentDate).isValid() && dateFormatter(record.activeConsentDate)
        )
    },
    {
      ...tableColumns[selectedViews.allApplications]?.consentStatus,
      render: (text, record) => renderWithLink(record, () => <ConsentStatusColumn record={record} />)
    },
    withSorting(sortingEnabled, tableColumns[selectedViews.allApplications]?.followupDate, {
      render: (text, record) => renderPatientCell("followupDate", record)
    }),
    {
      ...tableColumns[selectedViews.allApplications]?.primaryInsurancePayer,
      render: (text, record) =>
        renderWithLink(record, () => patientInsuranceCell(SUPPORTED_COVERAGES.primary)(text, record))
    },
    {
      ...tableColumns[selectedViews.allApplications]?.secondaryInsurancePayer,
      render: (text, record) =>
        renderWithLink(record, () => patientInsuranceCell(SUPPORTED_COVERAGES.secondary)(text, record))
    },
    {
      ...tableColumns[selectedViews.allApplications]?.tertiaryInsurancePayer,
      render: (text, record) =>
        renderWithLink(record, () => patientInsuranceCell(SUPPORTED_COVERAGES.tertiary)(text, record))
    },
    {
      ...tableColumns[selectedViews.allApplications]?.applicationSignatureStatus,
      render: (text, record) =>
        renderWithLink(
          record,
          () =>
            record.applicationSignatureStatus && <ESignatureStatusBadge status={record.applicationSignatureStatus} />
        )
    },
    withSorting(sortingEnabled, tableColumns[selectedViews.allApplications]?.latestClaimDate, {
      render: (text, record) =>
        renderWithLink(record, () => record.latestClaimDate && dateFormatter(record.latestClaimDate))
    }),
    {
      dataIndex: tableColumns[selectedViews.allApplications]?.edit?.dataIndex,
      key: tableColumns[selectedViews.allApplications]?.edit?.key,
      width: 80,
      className: "action-column",
      render: (_, record, rowIndex) => (
        <EditColumn
          isHovering={hoveringLineIndex === rowIndex}
          record={record}
          isFilterListView={isFilterListView}
          pageKey={PageKeys.ALL_ENROLLMENTS}
        />
      )
    }
  ];
  return (
    <>
      {!!tableData?.patients.length && (
        <ColumnSelectorContainer>
          <ColumnSelector />
        </ColumnSelectorContainer>
      )}
      <TableColumnWrapper containerRef={containerRef} reorderGuideLeft={reorderGuideLeft}>
        <StyledTable
          {...tableProps}
          size="small"
          rowClassName={(_, rowIndex) => `cursor-pointer${hoveringLineIndex === rowIndex ? " row-hovered" : ""}`}
          style={{ width: "100%" }}
          isFilterListView={isFilterListView}
          $hasSelection={rowSelection?.selectedRowKeys?.length > 0}
          rowKey="applicationId"
          rowSelection={rowSelection}
          loading={{ indicator: <TailormedSpinner />, spinning: isFetching }}
          components={components}
          columns={processColumns(
            columns.filter((col) => tableColumns[selectedViews.allApplications]?.[col.key]?.display)
          )}
          locale={{
            emptyText: <EmptyTableMessage type="enrollments" />
          }}
          onRow={(record, rowIndex) => {
            return {
              "data-pendo": "all_enrollments_row",
              "data-journey-id": record.journeyId,
              onMouseEnter: () => setHoveringLineIndex(rowIndex),
              onMouseLeave: () => setHoveringLineIndex(null)
            };
          }}
          onChange={(pagination, filters, sorter) =>
            fetchTableData(pagination, sorter, tableData, setTableDataAct, updateSavedFilterViewParameters)
          }
          pagination={{
            size: "small",
            showSizeChanger: false,
            current: tableData && tableData.selectedPage,
            defaultPageSize: PAGE_SIZE,
            pageSize: PAGE_SIZE,
            position: ["bottom"],
            total: totalRecordsCount,
            showTotal: (total, range) => (
              <div style={{ display: "flex", flexDirection: "row" }}>
                <PatientPageCount
                  total={total}
                  range={range}
                  isAccurateCount={tableData.isAccurateCount}
                  isEnrollment={true}
                />
              </div>
            )
          }}
          dataSource={
            tableData && tableData.patients
              ? tableData.patients
                  .map((patient, index) => {
                    return {
                      ...patient,
                      key: index
                    };
                  })
                  .sort((a, b) => b.isActive - a.isActive)
              : null
          }
        />
      </TableColumnWrapper>
    </>
  );
};

const mapStateToProps = createStructuredSelector({
  tableColumns: selectTableColumns,
  tableData: selectAllApplicationsData,
  isFetching: selectIsFetching([ApiNames.AllApplicationsPhiSearch, ApiNames.AllApplicationsSearch])
});

const mapDispatchToProps = (dispatch) => ({
  setTableDataAct: (page, sorter) => dispatch(ACTION.paginateSortChange(page, selectedViews.allApplications, sorter))
});

const AllApplicationsTable = compose(
  withTranslation(),
  connect(mapStateToProps, mapDispatchToProps)
)(AllApplicationsTableComponent);

export { AllApplicationsTable };
